import React from 'react';

import { Box, Link as MuiLink, Paper, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useIsMobile, mobileBreakpoint, useL10n } from '@uplift/hooks';
import { Button, MoneyCents } from '@uplift/ui-v2';

import { CardStatusBanner, UnpaidServicesBanner } from '../alerts';
import { useBillingContext } from '../billing.context';
import { balanceBoxWidth } from '../constants';


const NoBalance: React.VFC = () => {
  const l10n = useL10n();
  const { lastCompletedPaymentAt, isAutopayOn } = useBillingContext();

  return (
    <Paper elevation={0}>
      <Box px={{ xs: 2, sm: 0 }} py={{ xs: 2, sm: 0 }} width="100%">
        {isAutopayOn ? (
          <Typography variant="h4">{l10n.accountSettings.billing.autoPayOnNoPaymentDue}</Typography>
        ) : (
          <Typography variant="h4">{l10n.accountSettings.billing.noPaymentDue}</Typography>
        )}
        {lastCompletedPaymentAt && (
          <Box mt={1}>
            <Typography variant="body2" color="textPrimary">
              {l10n.accountSettings.billing.lastPaymentReceived(lastCompletedPaymentAt)}
            </Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
};


const Effortless: React.VFC<{ onAutopayOnClick: () => void }> = ({ onAutopayOnClick }) => {
  const isMobile = useIsMobile(mobileBreakpoint);
  const { classes } = useStyles();
  const l10n = useL10n();

  return (
    <Box display="flex" p={2} mx={{ xs: 2, sm: 0 }} mb={{ xs: 2, sm: 0 }} flexGrow={1} alignSelf="flex-end" className={classes.effortless} alignContent="center" flexDirection="column" gap="4px">
      <Typography className={classes.alertTitle}>
        {l10n.accountSettings.billing.effortlessPaymentsWithAutopay}
      </Typography>
      <Typography variant={isMobile ? 'body1' : 'body2'}>
        {l10n.accountSettings.billing.payBillsWithoutTheHassle}
      </Typography>
      <MuiLink className={classes.effortlessLink} onClick={onAutopayOnClick}>
        {l10n.accountSettings.billing.turnOnAutopay}
      </MuiLink>
    </Box>
  );
};


const Balance: React.VFC<{ onPayBalanceClick: () => void }> = ({ onPayBalanceClick }) => {
  const isMobile = useIsMobile(mobileBreakpoint);
  const l10n = useL10n();
  const { classes } = useStyles();
  const { payBalanceRef, balance, cardExpiredOrDeclined, lastCompletedPaymentAt } = useBillingContext();
  const lastPayment = lastCompletedPaymentAt ? (
    <Box width="100%" mt={3}>
      <Typography variant="body2">
        {l10n.accountSettings.billing.lastPaymentReceived(lastCompletedPaymentAt)}
      </Typography>
    </Box>
  ) : null;

  return (
    <Paper elevation={isMobile ? 0 : 3} style={{ flex: 1 }}>
      <Box display="flex" flexDirection="column" alignItems="center" py={3} px={isMobile ? 2 : 3} width={isMobile ? null : balanceBoxWidth}>
        <Typography variant="body1">
          {l10n.accountSettings.billing.outstandingBalance}
        </Typography>
        <Typography variant="h4" className={classes.balanceAmount}>
          <MoneyCents value={balance} />
        </Typography>
        <Button variant="contained" color="secondary" style={{ marginTop: '1.5rem' }} fullWidth ref={payBalanceRef} onClick={onPayBalanceClick}>
          {cardExpiredOrDeclined ? l10n.accountSettings.billing.editAndPay : l10n.accountSettings.billing.payBalance}
        </Button>
        {!isMobile && lastPayment}
      </Box>
    </Paper>
  );
};


export type CurrentBalanceProps = {
  onPayBalanceClick: () => void;
  onAutopayOnClick: () => void;
};


export const CurrentBalance: React.FC<CurrentBalanceProps> = ({ onPayBalanceClick, onAutopayOnClick }) => {
  const { isAutopayOn, balance } = useBillingContext();

  return (
    <>
      <CardStatusBanner />
      <UnpaidServicesBanner />
      <Box pb={{ xs: 0, sm: 5 }} />
      <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={{ xs: 0, sm: '2rem' }} width="100%">
        {balance > 0 ? (
          <Box minHeight="140px" py={1} display="flex" alignItems={{ xs: 'stretch', sm: 'center' }} flexDirection={{ xs: 'column', sm: 'row' }} gap={{ xs: 0, sm: '16px' }}>
            <Balance onPayBalanceClick={onPayBalanceClick} />
            {!isAutopayOn && <Effortless onAutopayOnClick={onAutopayOnClick} />}
          </Box>
        ) : (
          <Box minHeight="140px" py={1} display="flex" alignItems="center">
            <NoBalance />
          </Box>
        )}
      </Box>
    </>
  );
};

const useStyles = makeStyles()((theme) => ({
  findMoreSection: {
    marginTop: theme.spacing(0.5),
  },
  sessionsLink: {
    color: theme.palette.grey[600],
    textDecoration: 'underline',
    '&:visited': {
      color: theme.palette.grey[600],
    }
  },
  alertTitle: {
    fontWeight: 700,
    fontSize: '16px',
    lineHeight: '24px',
    letterSpacing: '0.4px',
    color: 'rgba(0, 0, 0, 0.87)',
  },
  effortlessLink: {
    ...theme.typography.body2 as React.CSSProperties,
    color: theme.palette.info.main,
    textDecoration: 'underline',
    fontWeight: 600,
    cursor: 'pointer',
    '&:hover': {
      color: theme.palette.info.main
    },
    '&:visited': {
      color: theme.palette.info.main
    }
  },
  effortless: {
    border: `1px solid ${theme.palette.info.border}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.info.background,
  },
  balanceAmount: {
    fontWeight: 500,
    fontSize: '3rem',
  },
}));
